import React, {
  forwardRef,
  Fragment,
  JSXElementConstructor,
  useState,
} from "react";
import * as RadioGroup from "@radix-ui/react-radio-group";
import type Stitches from "@stitches/react";
import { isEmpty, isNil } from "lodash";
import { Icon as LucideIcon, IconNames } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import {
  ComponentWrapper,
  LucideIconWrapper,
  SectionHeader,
  SelectionCard,
  SelectionCardTextBlock,
  StyledSelectionCardGroup,
} from "./__styles__/SelectionCardGroup";

export interface SelectionCardOption {
  /**
   * An ID value is required for the React `key` prop. Consider using
   * an actual unique identifier or a combination of the name of the
   * field and its value
   */
  id: string;
  value: string;
  title: string;
  description?: string;
  iconName?: IconNames;
  ghost?: boolean;
  leftComponent?: React.ReactNode;
  disabled?: boolean;
}

export interface SelectionCardGroupProps
  extends Stitches.VariantProps<typeof StyledSelectionCardGroup>,
    Omit<RadioGroup.RadioGroupProps, "onChange"> {
  options: Array<SelectionCardOption>;
  onChange: (value: string) => void;
  cardTitleProps?: Partial<React.ComponentProps<typeof Body>>;
  value?: string;
}

// Utility-type for combining props from Radix component props
// and styled component props
type RadixRefStyledComponentProps<
  RadixType,
  StyledComponent extends
    | keyof JSX.IntrinsicElements
    | JSXElementConstructor<any>,
> = Omit<RadixType, "asChild"> & React.ComponentProps<StyledComponent>;

type ForwardRefSelectionCardProps = RadixRefStyledComponentProps<
  RadioGroup.RadioGroupItemProps,
  typeof SelectionCard
> &
  SelectionCardOption & {
    cardTitleProps?: Partial<React.ComponentProps<typeof Body>>;
  };
const ForwardRefSelectionCard = forwardRef<
  HTMLButtonElement,
  ForwardRefSelectionCardProps
>((props, forwardedRef) => {
  const {
    id,
    value,
    title,
    description,
    iconName,
    leftComponent,
    cardTitleProps,
    disabled,
    ...refProps
  } = props;
  return (
    <SelectionCard
      {...refProps}
      ref={forwardedRef}
      type="button"
      disabled={disabled}
    >
      {iconName && (
        <LucideIconWrapper>
          <LucideIcon
            iconName={iconName}
            color={disabled ? "contentDisabled" : "contentSecondary"}
            size={16}
          />
        </LucideIconWrapper>
      )}
      {leftComponent && <ComponentWrapper>{leftComponent}</ComponentWrapper>}
      <SelectionCardTextBlock>
        <Body
          size="default"
          type="emphasis"
          style={{ marginBottom: 0 }}
          color={disabled ? "contentDisabled" : "contentPrimary"}
          {...cardTitleProps}
        >
          {title}
        </Body>
        {!isNil(description) && !isEmpty(description) && (
          <Body
            style={{ marginBottom: 0 }}
            color={disabled ? "contentDisabled" : "contentSecondary"}
            size="small"
            type="regular"
          >
            {description}
          </Body>
        )}
      </SelectionCardTextBlock>
    </SelectionCard>
  );
});

type ForwardRefSelectionCardGroupProps = RadixRefStyledComponentProps<
  RadioGroup.RadioGroupProps,
  typeof StyledSelectionCardGroup
> & {
  // property name pulled from console logging props passed to component
  "data-orientation"?: "vertical" | "horizontal";
};

const ForwardRefSelectionCardGroup = forwardRef<
  HTMLDivElement,
  ForwardRefSelectionCardGroupProps
>((props, forwardedRef) => {
  const direction =
    props["data-orientation"] === "vertical" ? "column" : undefined;

  return (
    <StyledSelectionCardGroup
      {...props}
      direction={direction}
      ref={forwardedRef}
    />
  );
});

const SelectionCardGroup = ({
  onChange,
  options,
  borderStyle,
  spacingStyle,
  cardTitleProps,
  ...radioGroupProps
}: SelectionCardGroupProps) => {
  const [selectedValue, setSelectedValue] = useState(
    radioGroupProps.value ?? radioGroupProps.defaultValue ?? "",
  );

  const handleValueChange = (newValue: string) => {
    const selectedOption = options.find(option => option.value === newValue);
    if (selectedOption && !selectedOption.disabled) {
      setSelectedValue(newValue);
      onChange(newValue);
    }
  };

  return (
    <RadioGroup.Root
      onValueChange={handleValueChange}
      {...radioGroupProps}
      asChild
      defaultValue={radioGroupProps.defaultValue ?? ""}
      value={selectedValue}
    >
      <ForwardRefSelectionCardGroup
        borderStyle={borderStyle}
        spacingStyle={spacingStyle}
      >
        {options.map(({ id, value, disabled, ...optionProps }) => {
          return (
            <RadioGroup.Item
              key={id}
              id={id}
              value={value}
              disabled={disabled}
              asChild
            >
              <ForwardRefSelectionCard
                id={id}
                value={value}
                disabled={disabled}
                cardTitleProps={cardTitleProps}
                {...optionProps}
              />
            </RadioGroup.Item>
          );
        })}
      </ForwardRefSelectionCardGroup>
    </RadioGroup.Root>
  );
};

SelectionCardGroup.displayName = "SelectionCardGroup";

export const SelectionCardGroupWithSections = ({
  onChange,
  groupedOptions,
  borderStyle,
  spacingStyle,
  cardTitleProps,
  ...radioGroupProps
}: Omit<SelectionCardGroupProps, "options"> & {
  groupedOptions: Record<string, Array<SelectionCardOption>>;
}) => {
  const [selectedValue, setSelectedValue] = useState(
    radioGroupProps.value ?? radioGroupProps.defaultValue ?? "",
  );

  const options = Object.values(groupedOptions).flat();
  const handleValueChange = (newValue: string) => {
    const selectedOption = options.find(option => option.value === newValue);
    if (selectedOption && !selectedOption.disabled) {
      setSelectedValue(newValue);
      onChange(newValue);
    }
  };

  return (
    <RadioGroup.Root
      onValueChange={handleValueChange}
      {...radioGroupProps}
      asChild
      defaultValue={radioGroupProps.defaultValue ?? ""}
      value={selectedValue}
    >
      <ForwardRefSelectionCardGroup
        borderStyle={borderStyle}
        spacingStyle={spacingStyle}
      >
        {Object.entries(groupedOptions).map(([section, options]) => (
          <Fragment key={section}>
            <SectionHeader spacingStyle={spacingStyle}>
              <Body size="small" type="emphasis">
                {section}
              </Body>
            </SectionHeader>

            {options.map(({ id, value, disabled, ...optionProps }) => {
              return (
                <RadioGroup.Item
                  key={id}
                  id={id}
                  value={value}
                  style={{ width: "100%" }}
                  disabled={disabled}
                  asChild
                >
                  <ForwardRefSelectionCard
                    id={id}
                    value={value}
                    disabled={disabled}
                    cardTitleProps={cardTitleProps}
                    {...optionProps}
                  />
                </RadioGroup.Item>
              );
            })}
          </Fragment>
        ))}
      </ForwardRefSelectionCardGroup>
    </RadioGroup.Root>
  );
};

export default SelectionCardGroup;
